import React, { useState, createContext, useRef, useCallback, useMemo } from "react";

const AudioPlayerContext = createContext();

const AudioPlayerProvider = ({ children }) => {
	const audioRef = useRef(null);
	const currentAudioRef = useRef(null);
	const seekTimeRef = useRef(null);
	const [currentAudio, setCurrentAudio] = useState(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [audioRate, setAudioRate] = useState(
		parseFloat(localStorage.getItem("audioMessageRate") || "1")
	);
	const audioRateRef = useRef(audioRate);

	const playAudio = useCallback((url, message, contactName, ticketUuid) => {
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
		let sourceUrl = url;
		if (isIOS) {
			sourceUrl = sourceUrl.replace(".ogg", ".mp3");
		}

		// If same audio is already playing, just toggle play/pause
		if (currentAudioRef.current && currentAudioRef.current.url === url && audioRef.current) {
			if (audioRef.current.paused) {
				audioRef.current.play();
			} else {
				audioRef.current.pause();
			}
			return;
		}

		// Stop previous audio
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		}

		const newAudio = {
			url,
			sourceUrl,
			message,
			contactName: contactName || "",
			isIOS,
			mediaType: "audio",
			ticketUuid: ticketUuid || null,
		};
		currentAudioRef.current = newAudio;
		setCurrentAudio(newAudio);
		setIsPlaying(true);
	}, []);

	const playVideo = useCallback((url, message, contactName, ticketUuid) => {
		// Stop previous audio if playing
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		}

		// If same video is already open, just toggle play/pause
		if (currentAudioRef.current && currentAudioRef.current.url === url && currentAudioRef.current.mediaType === "video") {
			// Video toggle is handled by the player itself
			return;
		}

		const newMedia = {
			url,
			sourceUrl: url,
			message,
			contactName: contactName || "",
			isIOS: false,
			mediaType: "video",
			ticketUuid: ticketUuid || null,
		};
		currentAudioRef.current = newMedia;
		setCurrentAudio(newMedia);
		setIsPlaying(true);
	}, []);

	const stopAudio = useCallback(() => {
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		}
		seekTimeRef.current = null;
		currentAudioRef.current = null;
		setCurrentAudio(null);
		setIsPlaying(false);
	}, []);

	const changeRate = useCallback(() => {
		let newRate;
		switch (audioRateRef.current) {
			case 0.5: newRate = 1; break;
			case 1: newRate = 1.5; break;
			case 1.5: newRate = 2; break;
			case 2: newRate = 0.5; break;
			default: newRate = 1; break;
		}
		audioRateRef.current = newRate;
		setAudioRate(newRate);
		localStorage.setItem("audioMessageRate", newRate);
		if (audioRef.current) {
			audioRef.current.playbackRate = newRate;
		}
	}, []);

	const contextValue = useMemo(() => ({
		audioRef,
		seekTimeRef,
		currentAudio,
		isPlaying,
		setIsPlaying,
		audioRate,
		playAudio,
		playVideo,
		stopAudio,
		changeRate,
	}), [currentAudio, isPlaying, audioRate, playAudio, playVideo, stopAudio, changeRate]);

	return (
		<AudioPlayerContext.Provider value={contextValue}>
			{children}
		</AudioPlayerContext.Provider>
	);
};

export { AudioPlayerContext, AudioPlayerProvider };
